'use client';

import { SanityImage } from '@workspace/interfaces';
import { cn } from '@workspace/lib';
import { GalleryView, GalleryHeader, GalleryThumbnails, GalleryDesktopArrows, GalleryMobileArrows } from './ui';
import { sanityImageSrc } from '@/sanity/lib/image';
import { useGallery } from './useGallery';
import type { GalleryProps } from './gallery.types';

export const Gallery = ({ block, isOpen, startIndex = 0, onRequestClose, imgSize }: GalleryProps) => {
  const {
    heading,
    backgroundColor,
    images = [],
    showNavigationArrows = true,
    navButtonClass = '',
    navIconSize = 24,
  } = block;

  const {
    isGalleryOpen,
    selectedIndex,
    gallerySlides,
    visibleThumbIndices,
    handleImageClick,
    onSlide,
    closeGallery,
    emblaRef,
    canScrollLeft,
    canScrollRight,
    scrollThumbnails,
    headingText,
    backgroundClass,
  } = useGallery({
    images: images as unknown as SanityImage[],
    isOpen,
    startIndex,
    onRequestClose,
    heading,
    backgroundColor,
  });

  if (!images?.length) return null;

  const buildImageUrl = (img: SanityImage | undefined, width: number, height: number) => {
    if (!img) return '';
    if (img.asset?._ref || img.asset?._id) {
      return sanityImageSrc(img, { width, height });
    }
    return img.asset?.url || img.url || '';
  };

  return (
    <section className={cn('gallery-container', backgroundClass)}>
      <div
        className={cn(
          'porta-container md:py-3xl md:pl-4xl md:pr-0 md:flex md:flex-row md:justify-center py-2xl pl-6 pr-0!',
        )}
      >
        {/* Left section */}
        <div className="flex flex-col gap-8 md:justify-between md:gap-[120px] md:mr-5xl md:text-left md:w-51.75!">
          <GalleryHeader headingText={headingText} />

          {/* Desktop-only arrows for page thumbnails */}
          {showNavigationArrows && (
            <GalleryDesktopArrows
              canScrollLeft={canScrollLeft}
              canScrollRight={canScrollRight}
              onScrollLeft={() => scrollThumbnails('left')}
              onScrollRight={() => scrollThumbnails('right')}
            />
          )}
        </div>

        {/* Thumbnails */}
        <GalleryThumbnails
          gallerySlides={gallerySlides}
          emblaRef={emblaRef}
          buildImageUrl={buildImageUrl}
          onImageClick={handleImageClick}
        />

        {/* Mobile-only arrows */}
        {showNavigationArrows && (
          <GalleryMobileArrows
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
            onScrollLeft={() => scrollThumbnails('left')}
            onScrollRight={() => scrollThumbnails('right')}
          />
        )}

        {/* Fullscreen modal / overlay */}
        <GalleryView
          isOpen={isGalleryOpen}
          gallerySlides={gallerySlides}
          selectedIndex={selectedIndex}
          showNavigationArrows={showNavigationArrows}
          navButtonClass={navButtonClass}
          navIconSize={navIconSize}
          onSlide={onSlide}
          onClose={closeGallery}
          visibleThumbIndices={visibleThumbIndices}
          imgSize={imgSize}
        />
      </div>
    </section>
  );
};
